<script lang="ts">
import { defineComponent } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import PageBreadcrumb from '@/components/common/PageBreadcrumb.vue'
import CompanyDataService from '@/services/CompanyDataService'
import HeadquartersDataService from '@/services/HeadquartersDataService'
import type Company from '@/types/Company'
import type Headquarters from '@/types/Headquarters'
import type ResponseData from '@/types/ResponseData'
import { vhqPackageOptions } from '@/utils/companyDetail'
import { toast } from 'vue3-toastify'
import 'vue3-toastify/dist/index.css'

export default defineComponent({
  name: 'vhq-detail',
  components: { PageBreadcrumb },
  data() {
    return {
      company: {} as Company,
      headquarters: {} as Headquarters,
      loaded: false,
      saving: false,
      router: useRouter(),
      route: useRoute(),
    }
  },
  computed: {
    companyId(): number {
      return Number(this.route.params.id)
    },
    packages(): string[] {
      return vhqPackageOptions(this.company.type, this.company.sidlo_typ_balika)
    },
    isDeactivated(): boolean {
      return !!this.company.sidlo_deaktivovane
    },
    isExpired(): boolean {
      if (!this.company.sidlo_zaplatene_do) return true
      return new Date(this.company.sidlo_zaplatene_do) < new Date()
    },
  },
  methods: {
    async loadData() {
      try {
        const compRes: ResponseData = await CompanyDataService.getCompanyById(this.companyId)
        this.company = compRes.data.data

        // Trim datetime to date-only for <input type="date">
        if (this.company.sidlo_zaplatene_do) {
          this.company.sidlo_zaplatene_do = this.company.sidlo_zaplatene_do.toString().substring(0, 10)
        }

        // Normalise package casing to match select options (Mini/Ideal/Premium/Extra)
        if (this.company.sidlo_typ_balika) {
          const map: Record<string, string> = { mini: 'Mini', ideal: 'Ideal', premium: 'Premium', extra: 'Extra', individual: 'Individual', 'základný trvalý pobyt v bratislave': 'Základný trvalý pobyt v Bratislave', 'najlepší trvalý pobyt v bratislave': 'Najlepší trvalý pobyt v Bratislave' }
          this.company.sidlo_typ_balika = map[this.company.sidlo_typ_balika.toLowerCase()] ?? this.company.sidlo_typ_balika
        }

        // Cast MySQL 1/0 integer to proper boolean so the Deaktivované checkbox renders correctly
        if (this.company.sidlo_deaktivovane !== null && this.company.sidlo_deaktivovane !== undefined) {
          this.company.sidlo_deaktivovane = Boolean(this.company.sidlo_deaktivovane)
        }

        if (this.company.headquarters_id) {
          const hqRes: ResponseData = await HeadquartersDataService.getHeadquartersById(this.company.headquarters_id)
          const hq = hqRes.data.data
          // Cast MySQL 1/0 integers to proper booleans so checkboxes render correctly
          for (const field of ['forwarding', 'scanning', 'shredding', 'picking_up_mails', 'e_data_mail_sk', 'active_on_address', 'marked_mailbox'] as const) {
            if (hq[field] !== null && hq[field] !== undefined) {
              hq[field] = Boolean(hq[field])
            }
          }
          this.headquarters = hq
        }
      } catch (e: any) {
        toast.error('Chyba pri načítaní: ' + e.message)
      } finally {
        this.loaded = true
      }
    },
    async save() {
      this.saving = true
      try {
        // Update company fields
        await CompanyDataService.update(this.company.id, {
          sidlo_typ_balika: this.company.sidlo_typ_balika,
          sidlo_zaplatene_do: this.company.sidlo_zaplatene_do,
          sidlo_deaktivovane: this.company.sidlo_deaktivovane,
        })

        // Update headquarters flags
        if (this.headquarters.id) {
          await HeadquartersDataService.update(this.headquarters.id, this.headquarters)
        }

        toast.success('Zmeny boli uložené.')
      } catch (e: any) {
        toast.error('Chyba pri ukladaní: ' + e.message)
      } finally {
        this.saving = false
      }
    },
    formatDate(date: string | null): string {
      if (!date) return '—'
      return new Date(date).toLocaleDateString('sk-SK')
    },
    goBack() {
      this.router.push({ name: 'VHQ Zoznam' })
    },
    goToCompany() {
      this.router.push({ name: 'Detail spoločnosti', params: { id: this.companyId } })
    },
  },
  mounted() {
    this.loadData()
  },
})
</script>

<template>
  <main class="flex-1 bg-gray-50 min-h-screen dark:bg-white/3">
    <div class="py-8">
      <div class="max-w-(--breakpoint-2xl) mx-auto">

        <!-- Header -->
        <div class="flex items-center gap-3 mb-6">
          <button
            type="button"
            @click="goBack"
            class="flex items-center py-2 px-4 border border-transparent rounded-md shadow-xs text-sm font-medium text-white bg-brand-600 hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500"
          >
            <svg class="size-4 mr-1" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
            </svg>
            Späť
          </button>
          <PageBreadcrumb page-title="VHQ Detail" />
          <button
            v-if="loaded"
            type="button"
            @click="goToCompany"
            class="ml-auto text-sm text-brand-600 hover:underline"
          >
            Otvoriť detail spoločnosti →
          </button>
        </div>

        <!-- Loading -->
        <div v-if="!loaded" class="flex justify-center py-20">
          <svg class="animate-spin size-8 text-brand-500" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
          </svg>
        </div>

        <div v-else class="space-y-6">

          <!-- Company info card (read-only) -->
          <div class="bg-white rounded-xl shadow-xs p-6 border border-gray-100 dark:bg-white/3 dark:border-gray-800">
            <h2 class="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2 dark:text-white/90">
              <svg class="size-5 text-brand-500" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M3 21V7a2 2 0 012-2h4l2-2h4l2 2h4a2 2 0 012 2v14M3 21h18" />
              </svg>
              Spoločnosť
            </h2>
            <dl class="grid grid-cols-1 sm:grid-cols-5 gap-4 text-sm">
              <div>
                <dt class="text-gray-500 font-medium dark:text-gray-400">Názov</dt>
                <dd class="text-gray-900 font-semibold dark:text-white/90">{{ company.name || '—' }}</dd>
              </div>
              <div>
                <dt class="text-gray-500 font-medium dark:text-gray-400">IČO</dt>
                <dd class="text-gray-900 dark:text-white/90">{{ company.ico || '—' }}</dd>
              </div>
              <div>
                <dt class="text-gray-500 font-medium dark:text-gray-400">Adresa</dt>
                <dd class="text-gray-900 dark:text-white/90">{{ headquarters.owner_name || '—' }}</dd>
              </div>
              <div>
                <dt class="text-gray-500 font-medium dark:text-gray-400">ID sídla (HQ)</dt>
                <dd class="text-gray-900 font-mono dark:text-white/90">{{ headquarters.id || '—' }}</dd>
              </div>
              <div>
                <dt class="text-gray-500 font-medium dark:text-gray-400">Stav sídla</dt>
                <dd>
                  <span
                    class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold"
                    :class="isDeactivated ? 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300' : isExpired ? 'bg-red-100 text-red-600' : 'bg-green-100 text-green-700'"
                  >
                    {{ isDeactivated ? 'Deaktivované' : isExpired ? 'Expirované' : 'Aktívne' }}
                  </span>
                </dd>
              </div>
            </dl>
          </div>

          <!-- Package & expiry -->
          <div class="bg-white rounded-xl shadow-xs p-6 border border-gray-100 dark:bg-white/3 dark:border-gray-800">
            <h2 class="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2 dark:text-white/90">
              <svg class="size-5 text-brand-500" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M20 7H4a2 2 0 00-2 2v10a2 2 0 002 2h16a2 2 0 002-2V9a2 2 0 00-2-2zM16 3H8l-2 4h12l-2-4z" />
              </svg>
              Balík a platnosť
            </h2>
            <div class="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">Balík</label>
                <FormKit
                  type="select"
                  v-model="company.sidlo_typ_balika"
                  name="sidlo_typ_balika"
                  :classes="{
                    outer: 'mt-1',
                    input: 'block w-full rounded-md border-gray-300 shadow-xs focus:border-brand-500 focus:ring-brand-500 sm:text-sm dark:border-gray-700'
                  }"
                >
                  <option v-for="p in packages" :key="p" :value="p">{{ p }}</option>
                </FormKit>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">Zaplatené do</label>
                <FormKit
                  type="date"
                  v-model="company.sidlo_zaplatene_do"
                  name="sidlo_zaplatene_do"
                  :classes="{
                    outer: 'mt-1',
                    input: 'block w-full rounded-md border-gray-300 shadow-xs focus:border-brand-500 focus:ring-brand-500 sm:text-sm dark:border-gray-700'
                  }"
                />
              </div>
              <div class="flex flex-col justify-end">
                <label class="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">Deaktivované</label>
                <div class="flex items-center mt-2">
                  <FormKit
                    id="deaktivovane"
                    type="checkbox"
                    v-model="company.sidlo_deaktivovane"
                    name="sidlo_deaktivovane"
                    :classes="{
                      outer: 'mb-0',
                      input: 'size-4 text-brand-600 border-gray-300 rounded-sm focus:ring-brand-500 dark:border-gray-700'
                    }"
                  />
                  <label for="deaktivovane" class="ml-2 text-sm text-gray-700 dark:text-gray-300">Deaktivované</label>
                </div>
              </div>
            </div>
          </div>

          <!-- Headquarters flags -->
          <div v-if="headquarters.id" class="bg-white rounded-xl shadow-xs p-6 border border-gray-100 dark:bg-white/3 dark:border-gray-800">
            <h2 class="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2 dark:text-white/90">
              <svg class="size-5 text-brand-500" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Nastavenia sídla
            </h2>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <!-- Address location -->
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">Adresa sídla (owner_name)</label>
                <FormKit
                  type="text"
                  v-model="headquarters.owner_name"
                  name="owner_name"
                  :classes="{
                    outer: 'mt-1',
                    input: 'block w-full rounded-md border-gray-300 shadow-xs focus:border-brand-500 focus:ring-brand-500 sm:text-sm dark:border-gray-700'
                  }"
                />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">Cena (€/mes)</label>
                <FormKit
                  type="text" number
                  v-model="headquarters.price"
                  name="price"
                  :classes="{
                    outer: 'mt-1',
                    input: 'block w-full rounded-md border-gray-300 shadow-xs focus:border-brand-500 focus:ring-brand-500 sm:text-sm dark:border-gray-700'
                  }"
                />
              </div>
            </div>

            <!-- Boolean toggles -->
            <div class="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-4">
<div v-for="flag in ['picking_up_mails', 'e_data_mail_sk', 'active_on_address', 'marked_mailbox'] as const" :key="flag" class="flex items-center gap-2">
                 <FormKit
                   :id="'flag-' + flag"
                   type="checkbox"
                   v-model="headquarters[flag] as boolean | undefined"
                   :name="flag"
                  :classes="{
                    outer: 'mb-0',
                    input: 'size-4 text-brand-600 border-gray-300 rounded-sm focus:ring-brand-500 dark:border-gray-700'
                  }"
                />
                <label :for="'flag-' + flag" class="text-sm text-gray-700 dark:text-gray-300">
                  {{ flag === 'picking_up_mails' ? 'Vyzdvihnutie' :
                     flag === 'e_data_mail_sk' ? 'E-schránka' :
                     flag === 'active_on_address' ? 'Aktívne na adrese' :
                     flag === 'marked_mailbox' ? 'Označená schránka' : flag }}
                </label>
              </div>
            </div>

            <!-- Custom note -->
            <div class="mt-6">
              <label class="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">Poznámka</label>
              <FormKit
                type="textarea"
                v-model="headquarters.custom_note as string | undefined"
                name="custom_note"
                rows="3"
                :classes="{
                  outer: 'mt-1',
                  input: 'block w-full rounded-md border-gray-300 shadow-xs focus:border-brand-500 focus:ring-brand-500 sm:text-sm dark:border-gray-700'
                }"
                placeholder="Interná poznámka k sídlu..."
              />
            </div>
          </div>

          <!-- Save button -->
          <div class="flex justify-end">
            <button
              type="button"
              :disabled="saving"
              @click="save"
              class="inline-flex items-center gap-2 py-2 px-6 border border-transparent shadow-xs text-sm font-semibold rounded-md text-white bg-brand-600 hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500 disabled:opacity-50"
            >
              <svg v-if="saving" class="animate-spin size-4" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
              </svg>
              <svg v-else class="size-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
              </svg>
              Uložiť zmeny
            </button>
          </div>

        </div>
      </div>
    </div>
  </main>
</template>
